import { httpClient } from './httpClient';
import type { MailOrderState } from '../stores/useMailOrderStore';
import { getSessionUser, type SessionUser } from '../utils/session';

const ACTIVATE_MAIL_ORDER_PATH = '/AccountInfo/ActivateMailOrder';

export interface MailOrderResponse {
  StatusCode?: string;
  StatusMessage?: string;
}

/**
 * Builds the legacy-compatible ActivateMailOrder payload from wizard store state.
 *
 * ## Card payment — eProtect token (NOT the raw card number)
 *
 * React never sends the user's raw card number to the backend.
 * The wizard uses the eProtect SDK to tokenize the card client-side before
 * submission. `eprotectResponse.paypageRegistrationId` is the one-time low-value
 * token (LVT) returned by eProtect and is placed into `payment.creditcard.lowvaluetoken`.
 * `vantivtxnid`, `binnumber`, `orderid`, and `reportgroup` are forwarded alongside it —
 * the backend needs all of these to exchange the LVT for a high-value token (HVT)
 * with eProtect/Vantiv (Worldpay).
 *
 * `orderId`/`id` sent to eProtect (see the `useEProtect` hook in
 * `src/features/billing/hooks/useEProtect.ts`) are pinned static values
 * (`'order123'` / `'123456'`), not generated per-call. A per-submit UUID was
 * tried and broke the eProtect request (suspected Vantiv field length/format
 * rejection) — do not change this without sandbox verification.
 *
 * Legacy Angular context: the legacy `activateMailorderController` sent the raw
 * card number directly in `cardnumber`. React corrects this by routing through
 * eProtect, which is the PCI-compliant approach.
 *
 * ## Check payment — sentinel values
 *
 * When `paymentType === 'check'`, creditcard fields are set to empty strings
 * and `expmonth`/`expyear` are set to `'-1'` as sentinel values. The backend
 * should treat these as a check/money-order enrollment (paymenttype `'1'`).
 *
 * ## Other invariants
 * - `expeditedshipping: false` always — removed from UI per Phase A decision.
 * - `medicationList: []` always — step dropped per Phase A (was dead code).
 * - `ApplicationToken` is required by the backend and read from `window.mxAppConfig`.
 * - `selectedUser` maps to the display name; `userDob` to the ISO DOB string.
 * - `demographics.email`, `planName`, and `carrierId` are read from the session
 *   account (not the wizard's selected user) — `SessionUser.EmailAddress`,
 *   `LakerGroupNumber` (the member-facing "Plan Name"), and `LakerCarrierCode`.
 *   The backend (VSDP-5921) renders these in the accounting email only when present.
 */
function buildPayload(state: MailOrderState): Record<string, unknown> {
  const {
    selectedUser,
    allergies,
    severity,
    conditions,
    paymentType,
    eprotectResponse,
    cardName,
    cardExpMonth,
    cardExpYear,
    cardLast4,
    demographics,
    acknowledgeTerms,
  } = state;

  const applicationToken = window.mxAppConfig?.ApplicationToken ?? '';
  const { orderId: eprotectOrderId = '' } = eprotectResponse ?? {};
  const sessionUser = getSessionUser<SessionUser>();

  // Credit card sub-object: eProtect token for card, empty/sentinel values for check
  const creditcard =
    paymentType === 'card'
      ? {
          cardname: cardName,
          lowvaluetoken: eprotectResponse?.paypageRegistrationId ?? '',
          vantivtxnid: eprotectResponse?.vantivTxnId ?? '',
          binnumber: eprotectResponse?.bin ?? '',
          last4: cardLast4,
          orderid: eprotectOrderId,
          reportgroup: eprotectResponse?.reportGroup ?? 'MPBI',
          expmonth: cardExpMonth,
          expyear: cardExpYear,
          notonfile: '',
        }
      : {
          cardname: '',
          lowvaluetoken: '',
          vantivtxnid: '',
          binnumber: '',
          last4: '',
          orderid: '',
          reportgroup: '',
          expmonth: '-1',
          expyear: '-1',
          notonfile: '',
        };

  return {
    ApplicationToken: applicationToken,
    selectedUser: selectedUser?.displayName ?? '',
    userDob: selectedUser?.dob ?? '',
    allergies,
    severity,
    conditions,
    medicationinfo: {
      rxnumber: '',
      medname: '',
      strength: '',
      doctor: '',
      pharmacy: '',
      pharmphone: '',
      brandonly: false,
      transfer: false,
    },
    medicationList: [],
    payment: {
      creditcard,
      paymenttype: paymentType === 'card' ? '0' : '1',
      expeditedshipping: false,
    },
    demographics: {
      phone: {
        areaCode: demographics.phone.areaCode,
        phoneNumber: demographics.phone.phoneNumber,
        extension: demographics.phone.extension,
      },
      address: {
        street1: demographics.address.street1,
        street2: demographics.address.street2,
        city: demographics.address.city,
        state: demographics.address.state,
        zipCode: demographics.address.zipCode,
      },
      email: sessionUser?.EmailAddress ?? '',
    },
    planName: sessionUser?.LakerGroupNumber ?? '',
    carrierId: sessionUser?.LakerCarrierCode ?? '',
    acknowledgeActivationTerms: acknowledgeTerms ? 'true' : '',
  };
}

export async function activateMailOrder(state: MailOrderState): Promise<MailOrderResponse> {
  const payload = buildPayload(state);
  const response = await httpClient.post<MailOrderResponse>(ACTIVATE_MAIL_ORDER_PATH, payload);
  return response.data;
}

// Export for testing
export { buildPayload };
